import { GROUND, GEAR_BASES, RECIPE_BASE_IDS, RECIPE_SABER, SABER, SABER_IDS, SABERES, canWearBase, saberFloor, type RiteId, type SaberId, type SaberSource } from '@somnhold/content';
import { idiv, imin, mulBp } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import { cellId } from '../grid.ts';
import { DIRS } from '../hex.ts';
import type { PlayerId } from '../types.ts';
import { event } from './core.ts';
import { activeHeroes, eraOf, groupLevel } from './eras.ts';
import type { CampaignState, Ms, SaberesRecord } from './types.ts';

/**
 * The saberes (docs/SABERES.md §5): every hard place with a chest adds to a hidden trail, once a day for the bastion
 * (the weekly ones once a week), and draws. With the trail at its floor one is sure. Terraced land only.
 */

/** Whether the bastion knows a saber. */
export const knowsSaber = (s: CampaignState, id: SaberId): boolean => !!s.saberes?.known.some((k) => k.id === id);

/** Whether saberes can be found yet: terraced land, the group at its level. */
export const saberesOpen = (s: CampaignState, now: Ms): boolean => !!s.map.land && now >= SABER.from && groupLevel(s, now) >= SABER.openGroup;

/** The saberes the bastion could find now: not known, their era reached, and a recipe only if an active hero can use it. */
export const saberPool = (s: CampaignState, now: Ms): SaberId[] => {
  const era = eraOf(s);
  const active = activeHeroes(s, now);
  return SABER_IDS.filter((id) => {
    if (knowsSaber(s, id)) return false;
    const def = SABERES[id];
    if (def.era > era) return false;
    // A recipe only if an active hero can wear its base.
    const base = RECIPE_BASE_IDS.find((b) => RECIPE_SABER[b] === id);
    if (base && GEAR_BASES[base] && !active.some((p) => canWearBase(p.hero.heroClass, base))) return false;
    return true;
  });
};

const record = (s: CampaignState): SaberesRecord => (s.saberes ??= { known: [], trail: 0, draws: 0, foundDay: -1, counted: {} });

/** The trail a source adds (the deep crypt per floor reached, the Chasm's weekly chest by its mark). */
export const saberTrailOf = (source: SaberSource, n = 1): number => {
  if (source === 'crypt3') return SABER.trail.crypt3 * n;
  if (source === 'sima') return n >= SABER.simaHighMark ? SABER.simaHigh : SABER.trail.sima;
  return SABER.trail[source];
};

/**
 * A hard place paid its chest: add to the trail (once a day, or a week, for the bastion) and draw. `day` is the
 * campaign day; `n`, the deep crypt's floors or the Chasm's mark. Returns the saber found, if any.
 */
export const saberChance = (s: CampaignState, source: SaberSource, by: PlayerId[], day: number, now: Ms, n = 1): SaberId | null => {
  if (!saberesOpen(s, now)) return null;
  if (source === 'sima' && n < SABER.simaMark) return null;
  const pool = saberPool(s, now);
  // Nothing left to find: the trail keeps still (saberes added to the game later find it waiting).
  if (pool.length === 0) return null;
  const r = record(s);
  const period = SABER.weekly.includes(source) ? idiv(day, 7) : day;
  if (r.counted[source] === period) return null;
  r.counted[source] = period;
  r.trail += saberTrailOf(source, n);
  // At most one a day: a second hit the same day finds nothing, and the trail stays.
  if (r.foundDay === day) return null;
  r.draws++;
  const rng = seedRng(deriveSeed(deriveSeed(s.seed, SABER.salt), r.draws));
  const floor = saberFloor(r.known.length);
  const chance = imin(SABER.chanceCapBp, r.trail * SABER.chancePerTrailBp);
  const roll = rngInt(rng, 10000);
  if (r.trail < floor && roll >= chance) return null;
  const id = pickSaber(s, pool, source, rngInt(rng, 1 << 30));
  if (!id) return null;
  const who = [...new Set(by)];
  if (!who.length) return null;
  r.known.push({ id, by: who, at: now, from: source });
  r.trail = SABERES[id].kind === 'custom' ? mulBp(r.trail, SABER.customKeepsBp) : 0;
  r.foundDay = day;
  event(s, now, 'saber', { pid: who[0], data: { id, from: source, by: who.join(',') } });
  return id;
};

/**
 * Which saber: its place weighs `placeWeight`, any other source 1. The first is never a custom, and never two of a
 * kind in a row while another kind is left.
 */
const pickSaber = (s: CampaignState, pool: SaberId[], source: SaberSource, roll: number): SaberId | null => {
  const known = s.saberes?.known ?? [];
  let cands = known.length === 0 ? pool.filter((id) => SABERES[id].kind !== 'custom') : pool;
  const last = known[known.length - 1];
  if (last) {
    const other = cands.filter((id) => SABERES[id].kind !== SABERES[last.id].kind);
    if (other.length) cands = other;
  }
  if (cands.length === 0) return null;
  let total = 0;
  const w = cands.map((id) => {
    const x = SABERES[id].place === source ? SABER.placeWeight : 1;
    total += x;
    return x;
  });
  let at = roll % total;
  for (let i = 0; i < cands.length; i++) {
    at -= w[i] as number;
    if (at < 0) return cands[i] as SaberId;
  }
  return cands[cands.length - 1] as SaberId;
};

/** The rite chosen at the keep for Marea `marea` as it is played now (a Marea played again has none), or null. */
export const riteFor = (s: CampaignState, marea: number): RiteId | null =>
  s.rite && s.rite.marea === marea && s.rite.repeat === (s.mareaRepeats ?? 0) ? s.rite.id : null;

/**
 * Whether the coming Marea's rite can still be chosen or undone: before its first wave (docs/SABERES.md §4.3). Its
 * deadline is the calendar's (`riteClosesAt`, checked by the server and the local game): the sim has no calendar.
 */
export const riteOpen = (s: CampaignState): boolean => s.status === 'active' && s.waveIndex === 0 && !s.mareaLost;

/** A rite made or undone: an army the general already drafted for the first wave is drafted again, under the rite. */
export const redraftForRite = (s: CampaignState): void => {
  if (s.draft && s.draft.marea === s.mareaIndex && s.draft.wave === 0) delete s.draft;
};

/** The saber that opens a structure, a branch or a recipe base, if any: it can't be laid or made before it is known. */
export const saberLocked = (s: CampaignState, id: SaberId | undefined): boolean => !!id && !knowsSaber(s, id);

/** Who names the cat: the first of who brought it still in the campaign (docs/SABERES.md §4.5). */
export const catNamer = (s: CampaignState): PlayerId | null => {
  const cat = s.saberes?.known.find((k) => k.id === 'cat');
  return cat?.by.find((pid) => s.players.some((p) => p.id === pid && !p.left)) ?? null;
};

/** Whether a boulder can sit here rolling toward `turn`: the next cell that way is one level lower (docs/SABERES.md §4.1). */
export const boulderSlope = (s: CampaignState, q: number, r: number, turn: number): boolean => {
  const land = s.map.land;
  const d = DIRS[turn];
  if (!land || !d) return false;
  const R = s.map.radius;
  const here = cellId(R, q, r);
  const next = cellId(R, q + d.q, r + d.r);
  if (here < 0 || next < 0) return false;
  return (land.level[here] ?? 0) - (land.level[next] ?? 0) === 1 && land.kind[next] !== GROUND.water;
};

/** Gates propped up for this Marea (docs/SABERES.md §4.3): not repaired between its waves. */
export const propsHold = (s: CampaignState): boolean => !!s.map.land && riteFor(s, s.mareaIndex) === 'props' && s.waveIndex > 0;
